/* eslint-disable react/display-name */
/* eslint-disable jsx-quotes */
import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useImperativeHandle,
} from "react";
import { Header, Dropdown, Form, Button, Input } from "semantic-ui-react";
import moment from "moment";
import ReactTable from "react-table-6";
import * as Util from "react-lib/utils";

// Controller
import DashboardController from "react-lib/apps/IsHealth/Dashboard/DashboardController";
import PRXManager from "react-lib/apis/manager/PRXManager";
import CoreManager from "react-lib/apis/manager/CoreManager";

// Common
import RightLabel from "react-lib/apps/common/RightLabel";
import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import ErrorMessage from "react-lib/apps/common/ErrorMessage";
import ModInfo from "react-lib/apps/common/ModInfo";

import { useIntl } from "react-intl";

// Types
interface PendingDetail {
  id: number;
  fullname: string;
  role: string;
  division: string;
  request_date: string;
}

interface MemberDetail {
  id: number;
  fullname: string;
  role: string;
  division: string;
  request_date: string;
}

interface PendingTableProps {
  lists: PendingDetail[];
  onApproveJoinOARequest: Function;
  onDeleteJoinOARequest: Function;
}

interface MemberProps {
  lists: MemberDetail[];
  roleOptions: any[];
  onRoleChange: Function;
  selectRow: any;
  onDeleteDivisionHasUserOA: Function;
}

type ManagePermissionsProps = {
  readOnly: boolean;
  division: number;
  controller: DashboardController & {
    prxManager: PRXManager;
    coreManager: CoreManager;
    getOAInviteLink: (params: { apiToken: string; divisionId: number }) => Promise<any>;
  };
  apiToken: string;
};

type FilterMemberKey =
  | "fullName"
  | "position"
  | "division"
  | "managePermission"
  | "schedulePermission";
type FieldType = string | ((data: any) => string | number);
type FilterMemberType = {
  [key in FilterMemberKey]: {
    value: string;
    field: FieldType;
    operator: "eq" | "in";
  };
};

const filterMemberInitial: FilterMemberType = {
  fullName: {
    value: "",
    field: (data) => `${data.user_profile?.first_name} ${data.user_profile?.last_name}`,
    operator: "in",
  },
  position: {
    value: "",
    field: "user_position.name",
    operator: "eq",
  },
  division: {
    value: "",
    field: "division.name",
    operator: "in",
  },
  managePermission: {
    value: "",
    field: "role",
    operator: "eq",
  },
  schedulePermission: {
    value: "",
    field: "",
    operator: "eq",
  },
};

// PendingTable Component
const PendingTable: React.FunctionComponent<PendingTableProps> = (props) => {
  const intl = useIntl();

  const headerStyle = {
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const columns = [
    {
      Header: "ชื่อ",
      accessor: "_full_name",
      headerStyle: headerStyle,
      Cell: ({ original }) => (
        <div>
          {original?.user_profile?.first_name} {original?.user_profile?.last_name}
        </div>
      ),
    },
    {
      Header: "ตำแหน่ง",
      accessor: " ",
      headerStyle: headerStyle,
      Cell: ({ original }) => <div>{original?.employee_position?.name}</div>,
    },
    {
      Header: "แผนก",
      accessor: "_division",
      headerStyle: headerStyle,
      Cell: ({ original }) => <div>{original?.division?.name}</div>,
    },
    {
      Header: "Request date",
      accessor: " ",
      headerStyle: headerStyle,
      Cell: ({ original }) => {
        const value = original?.created || "";
        return (
          <span className="dob">
            {value.indexOf("/") >= 0
              ? Util.formatDate(moment(value, "DD/MM/YYYY [[HH:mm]]"))
              : Util.formatDate(moment(value))}
          </span>
        );
      },
    },
    {
      Header: "Approve",
      accessor: "_approve",
      headerStyle: headerStyle,
      Cell: ({ original }) => (
        <Button basic color="green" onClick={() => props.onApproveJoinOARequest(original.id)}>
          Approve
        </Button>
      ),
    },
    {
      Header: "Delete",
      accessor: "_remove",
      headerStyle: headerStyle,
      Cell: ({ original }) => (
        <Button
          color="red"
          icon="minus"
          onClick={() => props.onDeleteJoinOARequest(original.id)}
        ></Button>
      ),
    },
  ];

  return (
    <div className="main-table">
      <ReactTable
        className="noHover"
        style={{ height: "250px" }}
        data={props.lists}
        columns={columns}
        pageSize={props.lists.length > 5 ? props.lists.length : 5}
        showPagination={false}
        noDataText="ไม่พบข้อมูลที่ค้นหา"
      />
    </div>
  );
};

// MemberTable Component
const MemberTable: FC<MemberProps> = (props) => {
  const intl = useIntl();
  const headerStyle = {
    padding: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const columns = [
    {
      Header: "ชื่อ",
      accessor: "fullname",
      headerStyle: headerStyle,
      Cell: ({ original }) => (
        <div>
          {original?.user_profile?.first_name} {original?.user_profile?.last_name}
        </div>
      ),
    },
    {
      Header: "ตำแหน่ง",
      accessor: " ",
      headerStyle: headerStyle,
      Cell: ({ original }) => <div>{original?.user_position?.name}</div>,
    },
    {
      Header: "สิทธิ์การลงตารางออกตรวจ",
      accessor: " ",
      headerStyle: headerStyle,
    },
    {
      Header: "แผนก",
      accessor: " ",
      headerStyle: headerStyle,
      Cell: ({ original }) => <div>{original?.division?.name}</div>,
    },
    {
      Header: "สิทธิ์การจัดการระบบ",
      accessor: " ",
      headerStyle: headerStyle,
      Cell: ({ original }) => (
        <div>
          <Dropdown
            search
            options={props.roleOptions}
            value={original.role}
            onChange={(e, { value }) => props.onRoleChange(original, value)}
          />
        </div>
      ),
    },
    {
      Header: "Delete",
      headerStyle: headerStyle,
      accessor: "_remove",
      Cell: ({ original }) => (
        <Button
          color="red"
          icon="minus"
          onClick={() => props.onDeleteDivisionHasUserOA(original.id)}
        ></Button>
      ),
    },
  ];

  const handleGetTdProps = (state: any, rowInfo: any, cellInfo: any) => {
    return {
      style: {
        overflow: "inherit",
      },
    };
  };

  const handleGetRowProps = (state: any, rowInfo: { original: { id: any } }) => {
    return {
      onClick: () => {},
      className:
        rowInfo?.original && rowInfo?.original?.id === props.selectRow?.id ? "selected" : "",
      style: {
        cursor: "pointer",
      },
    };
  };

  return (
    <div className="main-table">
      <ReactTable
        className="noHover"
        style={{ height: "250px" }}
        data={props.lists}
        columns={columns}
        pageSize={props.lists.length > 5 ? props.lists.length : 5}
        showPagination={false}
        noDataText={intl.formatMessage({ id: "ไม่พบข้อมูลที่ค้นหา" })}
        getTdProps={(state: any, rowInfo: any, column: any) => {
          return handleGetTdProps(state, rowInfo, column);
        }}
        getTrProps={(state: any, rowInfo: any, column: any, instance: any) => {
          return handleGetRowProps(state, rowInfo);
        }}
      />
    </div>
  );
};

// Main ManagePermissions Component
export const ManagePermissions = React.forwardRef<any, ManagePermissionsProps>((props, ref) => {
  const intl = useIntl();

  const [filterMember, setFilterMember] = useState<FilterMemberType>(filterMemberInitial);
  const [memberList, setMemberList] = useState<any[]>([]);
  const [pendingList, setPendingList] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);

  // States for role change
  const [selectedRowLocal, setSelectedRowLocal] = useState(null);
  const [roleId, setRoleId] = useState(null);

  // States for modals
  const [openModMemberRemove, setOpenModMemberRemove] = useState({
    open: false,
    error: null,
    id: null,
  });
  const [openModConfirmRemove, setOpenModConfirmRemove] = useState({
    open: false,
    id: null,
    error: null,
  });
  const [openModInfo, setOpenModInfo] = useState({
    open: false,
    error: null,
    color: null,
    title: null,
  });

  // States for invitation dialog
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const inviteInputBox = useRef<any>();

  useEffect(() => {
    if (props.division) {
      setFilterMember(filterMemberInitial);
    }
  }, [props.division]);

  // Get pending list function
  const getPendingList = useCallback(async () => {
    try {
      const [res, error] = await props.controller.prxManager.getListJoinOARequest({
        apiToken: props.apiToken,
        divisionId: props.division,
      });
      setPendingList(res?.items || []);
    } catch (error) {
      console.error("Error getting pending list:", error);
      setPendingList([]);
    }
  }, [props.apiToken, props.division, props.controller.prxManager]);

  // Get member list function
  const getMemberList = useCallback(async () => {
    try {
      const [res, error] = await props.controller.prxManager.getListDivisionHasUserOA({
        apiToken: props.apiToken,
        divisionId: props.division,
      });
      setMemberList(res?.items || []);
    } catch (error) {
      console.error("Error getting member list:", error);
      setMemberList([]);
    }
  }, [props.apiToken, props.division, props.controller.prxManager]);

  // Get user role function
  const getUserRole = useCallback(async () => {
    try {
      const [res, error] = await props.controller.prxManager.getListUserRoleOA({
        apiToken: props.apiToken,
        divisionId: props.division,
      });
      const options =
        res?.items?.map((item: { id: any; display_name: any }, index: any) => ({
          key: index,
          value: item.id,
          text: item.display_name,
        })) || [];
      setRoleOptions(options);
    } catch (error) {
      console.error("Error getting user roles:", error);
      setRoleOptions([]);
    }
  }, [props.apiToken, props.division, props.controller.prxManager]);

  // Load data when component mounts or division changes
  useEffect(() => {
    const getDataTable = async () => {
      if (props.division) {
        await getUserRole();
        await Promise.all([getPendingList(), getMemberList()]);
      }
    };
    getDataTable();
  }, [props.division, getPendingList, getMemberList, getUserRole]);

  // Expose functions via ref
  useImperativeHandle(ref, () => ({
    getPendingList: () => getPendingList(),
    getMemberList: () => getMemberList(),
    getUserRole: () => getUserRole(),
  }));

  useEffect(() => {
    const onFilterMemberList = (key?: FilterMemberKey, value?: string) => {
      const option: FilterMemberType = { ...filterMember };

      if (key) {
        option[key].value = value;
      }

      const filter = memberList.filter((list) => {
        let isCompare = true;
        const objKeys = Object.keys(option);

        objKeys.map((key) => {
          const { value, field, operator } = option[key as keyof FilterMemberType];
          const data = typeof field === "function" ? field(list) : objGet(list, field);

          if (value) {
            if (data === undefined) {
              objKeys.splice(0);
            }

            switch (operator) {
              case "eq":
                data === value ? (isCompare = true) : (isCompare = false);
                break;
              case "in":
                data.toString().toUpperCase().includes(value.toString().toUpperCase())
                  ? (isCompare = true)
                  : (isCompare = false);
                break;

              default:
                break;
            }
          }

          if (!isCompare) {
            objKeys.splice(0);
          }
          return [];
        });
        return isCompare;
      });

      setMemberList(filter);
    };

    onFilterMemberList();
  }, [filterMember]);

  const handleSetInputFilterMember = (
    key: FilterMemberKey,
    value: string | number | boolean | (string | number | boolean)[]
  ) => {
    setFilterMember({
      ...filterMember,
      [key]: {
        ...filterMember[key],
        value,
      },
    });
  };

  const handleKeyPressFullName = (ev: any) => {
    // Implementation if needed
  };

  const handleKeyPressDivision = (ev: any) => {
    // Implementation if needed
  };

  const objGet = (obj: any, keyString: string) => {
    let keys = keyString.split(".");
    return keys.reduce((result: { [x: string]: any }, key: string | number) => result?.[key], obj);
  };

  const createOption = (data: any[], key: string) => {
    const hasOption = new Map();
    const options = [
      {
        key: 0,
        text: "ไม่ระบุ",
        value: "",
      },
    ];

    for (const [index, list] of data.entries()) {
      const name = list?.[key]?.name;
      if (!hasOption.has(name) && name) {
        options.push({
          key: index,
          text: name,
          value: name,
        });
        hasOption.set(name, hasOption.size);
      }
    }

    return options;
  };

  const positionOptions = useMemo(() => {
    return createOption(memberList, "user_position");
  }, [memberList]);

  const roleOptionsForDropdown = useMemo(() => {
    return [
      {
        key: 0,
        text: "ไม่ระบุ",
        value: "",
      },
      ...(roleOptions || []),
    ];
  }, [roleOptions]);

  const getInvitationUrl = async () => {
    try {
      const [data] = await props.controller.getOAInviteLink({
        apiToken: props.apiToken,
        divisionId: props.division,
      });
      console.log(" data ", data);

      if (data?.token) {
        let origin = window.location.origin;
        setInviteLink(origin + "/?requestOA=" + data?.token);
        setOpenInviteDialog(true);
      } else {
        alert("ไม่สามารถเรียก URL จาก server ได้กรุณาลองอีกครั้ง");
      }
    } catch (error) {
      console.error("Error getting invitation URL:", error);
      alert("เกิดข้อผิดพลาดในการเรียก URL กรุณาลองอีกครั้ง");
    }
  };

  const handleCopyInviteLink = () => {
    if (inviteInputBox.current) {
      inviteInputBox.current.select();
      document.execCommand("copy");

      setTimeout(() => {
        setOpenInviteDialog(false);
      }, 1000);
    }
  };

  const handleCloseInviteDialog = () => {
    setOpenInviteDialog(false);
  };

  // Handle role change
  const handleChangeRole = async (row: any, roleId: any) => {
    const setRole = memberList.map((list) => {
      if (list.id === row.id) {
        return {
          ...list,
          role: roleId,
        };
      } else return list;
    });

    setMemberList(setRole);
    setSelectedRowLocal(row);
    setRoleId(roleId);
  };

  // Handle save role change
  const handleSaveRoleChange = async () => {
    if (selectedRowLocal?.id) {
      const [res, error] = await props.controller.prxManager.patchDivisionHasUserOA({
        apiToken: props.apiToken,
        id: selectedRowLocal.id,
        divisionId: props.division,
        roleId,
      });

      if (error) {
        setOpenModInfo({
          open: true,
          error,
          color: "red",
          title: "เกิดข้อผิดพลาด",
        });
        return;
      }

      setSelectedRowLocal(null);
      setRoleId(null);
      await getMemberList(); // Refresh member list
    }
  };

  // Handle approve join request
  const handleApproveJoinOARequest = async (id: number) => {
    const [res, error] = await props.controller.prxManager.postApproveJoinOARequest({
      apiToken: props.apiToken,
      divisionId: props.division,
      id,
    });
    if (error) {
      setOpenModInfo({
        open: true,
        error,
        color: "red",
        title: "เกิดข้อผิดพลาด",
      });
      return;
    }

    // Refresh both lists
    await Promise.all([getPendingList(), getMemberList()]);
  };

  // Handle delete join request
  const handleDeleteJoinOARequest = (id: number) => {
    setOpenModConfirmRemove({
      open: true,
      id,
      error: null,
    });
  };

  const handleConfirmDeleteJoinOARequest = async (id: number) => {
    const [res, error] = await props.controller.prxManager.deleteJoinOARequest({
      apiToken: props.apiToken,
      divisionId: props.division,
      id,
      detail: null,
    });
    if (error) {
      setOpenModConfirmRemove({
        open: true,
        id,
        error,
      });
      return;
    }
    setOpenModConfirmRemove({
      open: false,
      error: null,
      id: null,
    });

    await getPendingList(); // Refresh pending list
  };

  // Handle delete division has user
  const handleDeleteDivisionHasUserOA = (id: number) => {
    setOpenModMemberRemove({
      id,
      open: true,
      error: null,
    });
  };

  const handleConfirmDeleteDivisionHasUserOA = async (id: number) => {
    const [res, error] = await props.controller.prxManager.deleteDivisionHasUserOA({
      apiToken: props.apiToken,
      divisionId: props.division,
      id,
    });
    if (error) {
      setOpenModMemberRemove({
        open: true,
        id,
        error,
      });
      return;
    }

    handleCloseMemberRemoveMod();
    await getMemberList(); // Refresh member list
  };

  // Modal handlers
  const handleCloseMemberRemoveMod = () => {
    setOpenModMemberRemove({
      open: false,
      error: null,
      id: null,
    });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo({
      open: false,
      error: null,
      color: null,
      title: null,
    });
  };

  return (
    <>
      <div style={{ height: "100vh", overflowY: "auto" }}>
        <div className="dashboard" style={{ paddingBottom: "40px" }}>
          <Header as="h2" dividing style={{ color: "gray" }}>
            การจัดการสิทธิ
          </Header>
          <Header as="h2" dividing style={{ color: "gray" }}>
            คำร้องขอ ({pendingList.length})
          </Header>
          <PendingTable
            lists={pendingList}
            onApproveJoinOARequest={handleApproveJoinOARequest}
            onDeleteJoinOARequest={handleDeleteJoinOARequest}
          />
          <Form>
            <Form.Group inline>
              <Form.Field width={14}></Form.Field>
              <Form.Field width={2}>
                <Button basic color="blue" onClick={getInvitationUrl} style={{ marginTop: "15px" }}>
                  เชิญ User staff
                </Button>
              </Form.Field>
            </Form.Group>
          </Form>

          <Header as="h2" dividing style={{ color: "gray" }}>
            สมาชิก ({memberList.length})
          </Header>
          <Form className="filter-input">
            <Form.Group inline>
              <Form.Field width={2}>
                <RightLabel>{intl.formatMessage({ id: "ชื่อ-นามสกุล" })}</RightLabel>
              </Form.Field>
              <Form.Field width={4}>
                <Input
                  className="width100"
                  placeholder="Search"
                  value={filterMember.fullName.value}
                  onChange={(e, { value }) => handleSetInputFilterMember("fullName", value)}
                  onKeyPress={handleKeyPressFullName}
                />
              </Form.Field>
              <Form.Field width={2}>
                <RightLabel>{intl.formatMessage({ id: "ตำแหน่ง" })}</RightLabel>
              </Form.Field>
              <Form.Field width={3}>
                <Dropdown
                  className="fluidDropdown"
                  disabled={props.readOnly}
                  selection
                  value={filterMember.position.value}
                  options={positionOptions}
                  onChange={(e, { value }) => handleSetInputFilterMember("position", value)}
                />
              </Form.Field>
              <Form.Field width={2}>
                <RightLabel>{intl.formatMessage({ id: "สิทธิ์การลงตารางออกตรวจ" })}</RightLabel>
              </Form.Field>
              <Form.Field width={3}>
                <Dropdown
                  className="fluidDropdown"
                  disabled={props.readOnly}
                  selection
                  value={filterMember.schedulePermission.value}
                  options={[]}
                  onChange={(e, { value }) =>
                    handleSetInputFilterMember("schedulePermission", value)
                  }
                />
              </Form.Field>
            </Form.Group>

            <Form.Group inline>
              <Form.Field width={2}>
                <RightLabel>{intl.formatMessage({ id: "แผนก" })}</RightLabel>
              </Form.Field>
              <Form.Field width={4}>
                <Input
                  className="width100"
                  placeholder="Search"
                  value={filterMember.division.value}
                  onChange={(e, { value }) => handleSetInputFilterMember("division", value)}
                  onKeyPress={handleKeyPressDivision}
                />
              </Form.Field>
              <Form.Field width={2}>
                <RightLabel>{intl.formatMessage({ id: "สิทธิ์จัดการระบบ" })}</RightLabel>
              </Form.Field>
              <Form.Field width={7}>
                <Dropdown
                  className="fluidDropdown"
                  disabled={props.readOnly}
                  selection
                  value={filterMember.managePermission.value}
                  options={roleOptionsForDropdown}
                  onChange={(e, { value }) => handleSetInputFilterMember("managePermission", value)}
                />
              </Form.Field>
            </Form.Group>
          </Form>
          <MemberTable
            lists={memberList}
            roleOptions={roleOptions}
            onRoleChange={handleChangeRole}
            selectRow={selectedRowLocal}
            onDeleteDivisionHasUserOA={handleDeleteDivisionHasUserOA}
          />

          <Form>
            <Form.Group inline>
              <Form.Field width={14}></Form.Field>
              <Form.Field width={2}>
                <Button
                  color="blue"
                  className="right aligned"
                  type="button"
                  onClick={handleSaveRoleChange}
                  disabled={selectedRowLocal?.id ? false : true}
                  style={{ marginTop: "15px" }}
                >
                  บันทึก
                </Button>
              </Form.Field>
            </Form.Group>
          </Form>
        </div>
      </div>

      {/* ModConfirm dialog for invitation link */}
      <ModConfirm
        openModal={openInviteDialog}
        titleName="Copy Link"
        size="small"
        denyButtonColor="orange"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        approveButtonColor="blue"
        approveButtonText={intl.formatMessage({ id: "คัดลอก" })}
        content={
          <Form>
            <Form.Field width={16}>
              <Input ref={inviteInputBox} value={inviteLink} />
            </Form.Field>
          </Form>
        }
        onApprove={handleCopyInviteLink}
        onDeny={handleCloseInviteDialog}
      />

      {/* Modal for confirming delete join request */}
      <ModConfirm
        openModal={openModConfirmRemove.open}
        titleName={intl.formatMessage({ id: "ต้องการลบหรือไม่" })}
        size="mini"
        denyButtonColor="orange"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        approveButtonColor="blue"
        approveButtonText={intl.formatMessage({ id: "ตกลง" })}
        content={<ErrorMessage error={openModConfirmRemove.error} />}
        onApprove={() => {
          handleConfirmDeleteJoinOARequest(openModConfirmRemove.id);
        }}
        onDeny={() => {
          setOpenModConfirmRemove({
            open: false,
            error: null,
            id: null,
          });
        }}
      />

      {/* Modal for confirming delete member */}
      <ModConfirm
        openModal={openModMemberRemove.open}
        titleName={intl.formatMessage({ id: "ต้องการลบหรือไม่" })}
        size="mini"
        denyButtonColor="orange"
        denyButtonText={intl.formatMessage({ id: "ยกเลิก" })}
        approveButtonColor="blue"
        approveButtonText={intl.formatMessage({ id: "ตกลง" })}
        content={<ErrorMessage error={openModMemberRemove.error} />}
        onApprove={() => {
          handleConfirmDeleteDivisionHasUserOA(openModMemberRemove.id);
        }}
        onDeny={handleCloseMemberRemoveMod}
        onCloseWithDimmerClick={handleCloseMemberRemoveMod}
      />

      {/* Info modal for showing messages */}
      <ModInfo
        open={openModInfo.open}
        titleColor={openModInfo.color || "red"}
        titleName={openModInfo.title}
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        <ErrorMessage error={openModInfo.error} />
      </ModInfo>
    </>
  );
});

ManagePermissions.displayName = "ManagePermissions";

export default ManagePermissions;
